import React from 'react';
import { METRICS, fmtVal } from './fmt';

const TITLES = { gender: 'Male vs Female', worker_type: 'Regular vs Contract', collar: 'White vs Blue collar' };
const BAR = [['bg-indigo-500', 'text-indigo-700'], ['bg-amber-500', 'text-amber-700']];

const Side = ({ s, max, metric, tone, active, onPick, testId }) => (
  <button type="button" onClick={onPick} disabled={!s.staff} title={s.staff ? `Show only ${s.label} staff` : 'No ranked staff'}
    className={`w-full text-left rounded-lg px-2 py-1.5 transition-colors ${active ? 'bg-indigo-50 ring-1 ring-indigo-300' : 'hover:bg-gray-50'} disabled:cursor-default disabled:hover:bg-transparent`}
    data-testid={testId}>
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="font-semibold text-gray-700">{s.label}</span>
      <span className={`font-bold text-sm ${tone[1]}`} data-testid={`${testId}-avg`}>{fmtVal(s.avg, metric)}</span>
    </div>
    <div className="h-2 rounded-full bg-gray-100 mt-1 overflow-hidden">
      <div className={`h-full rounded-full ${tone[0]} transition-[width] duration-500`} style={{ width: `${max && s.avg ? Math.max(4, (s.avg / max) * 100) : 0}%` }} />
    </div>
    <div className="text-[11px] text-gray-500 mt-1" data-testid={`${testId}-meta`}>
      {s.staff} staff{s.low ? ` · ${s.low} low` : ''}{s.repeat_low ? ` · ${s.repeat_low} repeat low` : ''}
    </div>
  </button>
);

const Gap = ({ g }) => {
  if (g.gap_pct == null) return <span className="text-gray-400">Not enough staff to compare</span>;
  if (g.gap_pct === 0) return <span className="text-gray-600">Same average</span>;
  const [hi, lo] = g.gap_pct > 0 ? g.sides : [g.sides[1], g.sides[0]];
  const pct = g.gap_pct > 0 ? g.gap_pct : Math.round((g.sides[1].avg / g.sides[0].avg - 1) * 100);
  return <span className="text-gray-700"><b>{hi.label}</b> {pct}% ahead of {lo.label}</span>;
};

export const GroupComparison = ({ op, metric, staffFilters = {}, onPick }) => {
  const groups = op.groups || [];
  if (!groups.some((g) => g.sides.some((s) => s.staff))) {
    return (
      <div className="mb-3 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-xs text-gray-500" data-testid="rank-groups-empty">
        Male vs Female, Regular vs Contract and White vs Blue collar comparisons appear once these are filled in Staff Master — none of the {op.ranked} ranked staff here have them yet.
      </div>
    );
  }
  return (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" data-testid="rank-groups">
    {groups.map((g) => {
      const max = Math.max(...g.sides.map((s) => s.avg || 0));
      if (!g.sides.some((s) => s.staff)) {
        return (
          <div key={g.field} className="bg-white rounded-xl border border-dashed border-gray-300 p-3 text-xs text-gray-500" data-testid={`rank-group-${g.field}`}>
            <div className="font-semibold text-gray-500 uppercase tracking-wide mb-1">{TITLES[g.field]}</div>
            Not filled in Staff Master for the {g.not_set} ranked staff.
          </div>
        );
      }
      return (
        <div key={g.field} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`rank-group-${g.field}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{TITLES[g.field]}</span>
            <span className="text-[11px] text-gray-400">{METRICS[metric].short} · period avg</span>
          </div>
          {g.sides.map((s, i) => (
            <Side key={s.value} s={s} max={max} metric={metric} tone={BAR[i]} active={staffFilters[g.field] === s.value}
              onPick={() => onPick(g.field, staffFilters[g.field] === s.value ? 'all' : s.value)} testId={`rank-group-${g.field}-${s.value}`} />
          ))}
          <div className="flex items-center justify-between gap-2 text-xs mt-1.5 pt-1.5 border-t border-gray-100">
            <span data-testid={`rank-group-${g.field}-gap`}><Gap g={g} /></span>
            {g.not_set > 0 && <span className="text-gray-400 whitespace-nowrap" data-testid={`rank-group-${g.field}-notset`}>{g.not_set} not set</span>}
          </div>
        </div>
      );
    })}
  </div>
  );
};

export default GroupComparison;
